/**
 * Subscription Store
 * Manages subscription state, limits, and feature access
 * All limits and features come from the backend - nothing is hardcoded
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { subscriptionService } from '@/services/subscription-service';
import type {
  SubscriptionStatus,
  Plan,
  Subscription,
  SubscriptionAlert,
  UserLimits,
  FeatureAccessResult,
} from '@/types/subscription';

interface SubscriptionState {
  // Subscription status
  isActive: boolean;
  status: SubscriptionStatus;
  plan: Plan | null;
  subscription: Subscription | null;
  alerts: SubscriptionAlert[];

  // Limits (from backend)
  limits: UserLimits | null;

  // Feature access cache
  featureCache: Map<string, boolean>;

  // Loading states
  isLoading: boolean;
  isLoadingLimits: boolean;

  // Timestamps
  lastFetched: number | null;
  lastLimitsFetched: number | null;

  // Actions
  fetchStatus: () => Promise<void>;
  fetchLimits: () => Promise<UserLimits | null>;
  checkFeature: (feature: string) => Promise<boolean>;
  retryPayment: (subscriptionId: string, cardToken?: string) => Promise<boolean>;
  updateLocale: (locale: 'pt-BR' | 'en' | 'es') => Promise<void>;
  clearAlerts: () => void;
  reset: () => void;

  // Selectors
  canAccessFeature: (feature: string) => FeatureAccessResult;
  isAtLimit: (limitType: 'items' | 'vaults' | 'storage' | 'sharedUsers') => boolean;
  getUsagePercentage: (limitType: 'items' | 'vaults' | 'storage' | 'sharedUsers') => number;
}

const initialState = {
  isActive: false,
  status: 'free' as SubscriptionStatus,
  plan: null,
  subscription: null,
  alerts: [],
  limits: null,
  featureCache: new Map<string, boolean>(),
  isLoading: false,
  isLoadingLimits: false,
  lastFetched: null,
  lastLimitsFetched: null,
};

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      ...initialState,

      fetchStatus: async () => {
        set({ isLoading: true });

        try {
          const response = await subscriptionService.getStatus();

          set({
            isActive: response.isActive,
            status: response.status,
            plan: response.plan,
            subscription: response.subscription,
            alerts: response.alerts,
            lastFetched: Date.now(),
          });
        } catch (error) {
          console.error('Failed to fetch subscription status:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      fetchLimits: async () => {
        set({ isLoadingLimits: true });

        try {
          const limits = await subscriptionService.getLimits();

          set({
            limits,
            lastLimitsFetched: Date.now(),
          });

          return limits;
        } catch (error) {
          console.error('Failed to fetch limits:', error);
          return null;
        } finally {
          set({ isLoadingLimits: false });
        }
      },

      checkFeature: async (feature: string) => {
        const { featureCache } = get();

        // Check cache first
        if (featureCache.has(feature)) {
          return featureCache.get(feature)!;
        }

        try {
          const response = await subscriptionService.checkFeature(feature);

          // Update cache
          const newCache = new Map(featureCache);
          newCache.set(feature, response.hasAccess);
          set({ featureCache: newCache });

          return response.hasAccess;
        } catch (error) {
          console.error(`Failed to check feature ${feature}:`, error);
          return false;
        }
      },

      retryPayment: async (subscriptionId, cardToken) => {
        set({ isLoading: true });

        try {
          const response = await subscriptionService.retryPayment({
            subscriptionId,
            cardToken,
          });

          if (response.success) {
            // Refresh status after successful payment
            await get().fetchStatus();
            await get().fetchLimits();
            return true;
          }

          return false;
        } catch (error) {
          console.error('Failed to retry payment:', error);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      updateLocale: async (locale) => {
        try {
          await subscriptionService.updateLocale({ locale });
        } catch (error) {
          console.error('Failed to update locale:', error);
          throw error;
        }
      },

      clearAlerts: () => {
        set({ alerts: [] });
      },

      reset: () => {
        set({
          ...initialState,
          featureCache: new Map<string, boolean>(),
        });
      },

      canAccessFeature: (feature) => {
        const { featureCache, isActive, plan } = get();

        // Check cache
        const hasFeature = featureCache.get(feature);

        // If we have cached value, use it
        if (hasFeature !== undefined) {
          if (hasFeature) {
            return { canAccess: true };
          }
          return {
            canAccess: false,
            reason: 'upgrade_required',
            upgradeUrl: '/app/settings/billing',
          };
        }

        // If on free plan, assume premium features are locked
        if (plan?.isFree) {
          return {
            canAccess: false,
            reason: 'upgrade_required',
            upgradeUrl: '/app/settings/billing',
          };
        }

        // If subscription is active, allow access (will be verified by backend on API calls)
        if (isActive) {
          return { canAccess: true };
        }

        // Subscription expired or cancelled
        return {
          canAccess: false,
          reason: 'subscription_expired',
          upgradeUrl: '/app/settings/billing',
        };
      },

      isAtLimit: (limitType) => {
        const { limits } = get();
        if (!limits) return false;

        // Handle storage separately due to different property names
        if (limitType === 'storage') {
          const storage = limits.storage;
          if (storage.unlimited || storage.maxBytes === null) return false;
          return storage.currentBytes >= storage.maxBytes;
        }

        const limit = limits[limitType];
        if (!limit || limit.unlimited || limit.max === null) return false;

        return limit.current >= limit.max;
      },

      getUsagePercentage: (limitType) => {
        const { limits } = get();
        if (!limits) return 0;

        // Handle storage separately due to different property names
        if (limitType === 'storage') {
          const storage = limits.storage;
          if (storage.unlimited || storage.maxBytes === null) return 0;
          return Math.min(100, (storage.currentBytes / storage.maxBytes) * 100);
        }

        const limit = limits[limitType];
        if (!limit || limit.unlimited || limit.max === null) return 0;

        return Math.min(100, (limit.current / limit.max) * 100);
      },
    }),
    {
      name: 'subscription-storage',
      partialize: (state) => ({
        isActive: state.isActive,
        status: state.status,
        plan: state.plan,
        subscription: state.subscription,
        limits: state.limits,
        lastFetched: state.lastFetched,
        lastLimitsFetched: state.lastLimitsFetched,
        // Don't persist alerts or feature cache - always fetch fresh
      }),
    }
  )
);
